import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, SectionHeader, PrimaryBtn, IconBtn, Field } from "./ui";

export default function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const empty = { title: "", category: "Actualite", excerpt: "", content: "", image: "" };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => { api.get("/news").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (n) => { setEditing(n); setForm({ ...n }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    const payload = { title: form.title, category: form.category, excerpt: form.excerpt, content: form.content, image: form.image };
    try {
      if (editing) await api.put(`/news/${editing.id}`, payload);
      else await api.post("/news", payload);
      toast.success(editing ? "Actualité mise à jour" : "Actualité créée");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (n) => { if (!window.confirm(`Supprimer "${n.title}" ?`)) return; await api.delete(`/news/${n.id}`); toast.success("Actualité supprimée"); load(); };

  return (
    <Card>
      <SectionHeader title="Actualités" action={<PrimaryBtn onClick={openNew} data-testid="admin-new-news"><Plus className="w-4 h-4" /> Nouvelle actualité</PrimaryBtn>} />
      <div className="space-y-3" data-testid="admin-news-list">
        {items.map((n) => (
          <div key={n.id} className="flex items-center gap-4 border border-slate-100 rounded-2xl p-4">
            <img src={n.image} alt="" className="w-14 h-14 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-slate-900 truncate">{n.title}</p>
              <p className="text-xs text-amber-600 font-semibold">{n.category}</p>
            </div>
            <IconBtn icon={Pencil} color="sky" onClick={() => openEdit(n)} testid={`admin-edit-news-${n.id}`} />
            <IconBtn icon={Trash2} color="rose" onClick={() => remove(n)} testid={`admin-delete-news-${n.id}`} />
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Modifier l'actualité" : "Nouvelle actualité"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="admin-news-form">
            <Field label="Titre"><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1" data-testid="nf-title" /></Field>
            <Field label="Catégorie"><Input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl mt-1" data-testid="nf-category" /></Field>
            <Field label="Extrait"><Textarea required rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="rounded-xl mt-1" data-testid="nf-excerpt" /></Field>
            <Field label="Contenu"><Textarea required rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="rounded-xl mt-1" data-testid="nf-content" /></Field>
            <Field label="Image (URL)"><Input required value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="rounded-xl mt-1" data-testid="nf-image" /></Field>
            <PrimaryBtn type="submit" data-testid="nf-submit">{editing ? "Enregistrer" : "Créer"}</PrimaryBtn>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
